(function () {
  'use strict';

  var root = document.documentElement;
  var reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var revealObserver = null;

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList || []);
  }

  function uniqueBySelector(scope, selectors) {
    var map = new Set();
    selectors.forEach(function (selector) {
      toArray(scope.querySelectorAll(selector)).forEach(function (node) {
        map.add(node);
      });
    });
    return Array.from(map);
  }

  function addClass(node, className) {
    if (node && !node.classList.contains(className)) {
      node.classList.add(className);
    }
  }

  function markArrowTargets(button) {
    var iconTargets = uniqueBySelector(button, [
      '.icon-arrow',
      '.icon-caret',
      '.icon-wrap',
      '.svg-wrapper',
      '[class*="icon"] svg',
      'svg'
    ]);

    iconTargets.forEach(function (icon) {
      if (!icon.hasAttribute('data-ks-arrow')) {
        icon.setAttribute('data-ks-arrow', '');
      }
    });
  }

  function enhanceButtons(scope) {
    var buttonSelectors = [
      'a.button',
      'button.button',
      '.button--secondary',
      '.shopify-challenge__button',
      '.contact__button button',
      'button.shopify-payment-button__button--unbranded'
    ];

    uniqueBySelector(scope, buttonSelectors).forEach(function (button) {
      addClass(button, 'ksc-button-enhanced');
      markArrowTargets(button);
    });
  }

  function enhanceCardsAndImages(scope) {
    uniqueBySelector(scope, [
      '.card',
      '.multicolumn-card',
      '.content-container',
      '.article-card-wrapper .card',
      '.collection-card-wrapper .card',
      '.product-card-wrapper .card'
    ]).forEach(function (card) {
      addClass(card, 'ksc-card-hover');
    });

    uniqueBySelector(scope, [
      '.card__media',
      '.multicolumn-card__image-wrapper',
      '.image-with-text__media',
      '.banner__media',
      '.article-card__image-wrapper',
      '.collection-hero__image-container'
    ]).forEach(function (media) {
      if (media.classList.contains('ksc-image-hover--has-overlay')) return;
      addClass(media, 'ksc-image-hover');
      addClass(media, 'ksc-overlay-fade');
      addClass(media, 'image-container-hover');
    });
  }

  function enhanceHeaderAndNav(scope) {
    toArray(scope.querySelectorAll('.header-wrapper')).forEach(function (header) {
      addClass(header, 'ksc-header-transition');
    });

    uniqueBySelector(scope, [
      '.header__menu-item',
      '.header__submenu .list-menu__item',
      '.menu-drawer__menu-item'
    ]).forEach(function (item) {
      addClass(item, 'ksc-nav-link');
    });
  }

  function prepareReveal(scope) {
    var revealSelectors = [
      '.section-header h1',
      '.section-header h2',
      '.section-header h3',
      '.image-with-text__heading',
      '.image-with-text__text',
      '.rich-text__heading',
      '.rich-text__text',
      '.collapsible-content__heading',
      '.multicolumn .title',
      '.contact .title'
    ];

    uniqueBySelector(scope, revealSelectors).forEach(function (node) {
      if (node.closest('.header-wrapper, .menu-drawer, .footer')) return;
      addClass(node, 'ksc-reveal');
      addClass(node, 'ksc-reveal-up');
    });

    var groups = [
      { container: '.multicolumn-list', children: ':scope > li' },
      { container: '.grid.product-grid', children: ':scope > li' },
      { container: '.blog-articles', children: ':scope > *' },
      { container: '.list-unstyled.article-template__social-sharing', children: ':scope > *' }
    ];

    groups.forEach(function (groupDef) {
      toArray(scope.querySelectorAll(groupDef.container)).forEach(function (group) {
        addClass(group, 'ksc-reveal-group');
        toArray(group.querySelectorAll(groupDef.children)).forEach(function (child, index) {
          addClass(child, 'ksc-reveal');
          addClass(child, 'ksc-reveal-up');
          child.style.setProperty('--ks-reveal-index', String(index));
        });
      });
    });
  }

  function showReveal(node) {
    node.classList.add('ksc-reveal-visible');
  }

  function setupRevealObserver(scope) {
    var targets = toArray(scope.querySelectorAll('.ksc-reveal'));
    if (!targets.length) return;

    if (reducedMotionQuery.matches || !('IntersectionObserver' in window)) {
      targets.forEach(showReveal);
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            showReveal(entry.target);
            revealObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
      );
    }

    targets.forEach(function (target) {
      if (target.classList.contains('ksc-reveal-visible')) return;
      revealObserver.observe(target);
    });
  }

  function ensureSummaryIcon(summary) {
    if (!summary) return;
    if (summary.querySelector('.icon-caret, .ksc-accordion-icon')) return;
    var icon = document.createElement('span');
    icon.className = 'ksc-accordion-icon';
    icon.setAttribute('aria-hidden', 'true');
    summary.appendChild(icon);
  }

  function updateDetailsContent(details, content, instant) {
    var open = details.hasAttribute('open');
    var nextHeight = open ? content.scrollHeight : 0;
    content.hidden = false;

    if (instant || reducedMotionQuery.matches) {
      content.style.maxHeight = open ? 'none' : '0px';
      content.style.opacity = open ? '1' : '0';
      return;
    }

    var fromHeight = content.offsetHeight;
    content.style.maxHeight = fromHeight + 'px';
    content.style.opacity = open ? '0' : '1';

    requestAnimationFrame(function () {
      content.style.maxHeight = nextHeight + 'px';
      content.style.opacity = open ? '1' : '0';
    });

    var onEnd = function (event) {
      if (event.propertyName !== 'max-height') return;
      content.style.maxHeight = open ? 'none' : '0px';
      content.removeEventListener('transitionend', onEnd);
    };

    content.addEventListener('transitionend', onEnd);
  }

  function bindDetailsAccordion(scope) {
    toArray(scope.querySelectorAll('.accordion, .collapsible-content, .product__accordion')).forEach(function (container) {
      if (!container.querySelector('details')) return;
      addClass(container, 'ksc-accordion-enhanced');
    });

    toArray(scope.querySelectorAll('.ksc-accordion-enhanced details')).forEach(function (details) {
      if (details.dataset.ksAccordionBound === 'true') return;
      details.dataset.ksAccordionBound = 'true';

      var summary = details.querySelector('summary');
      var content = details.querySelector('.accordion__content, .ksc-accordion-content');
      if (!summary || !content) return;

      ensureSummaryIcon(summary);
      addClass(content, 'ksc-accordion-content');
      summary.setAttribute('aria-expanded', details.hasAttribute('open') ? 'true' : 'false');
      updateDetailsContent(details, content, true);

      details.addEventListener('toggle', function () {
        summary.setAttribute('aria-expanded', details.hasAttribute('open') ? 'true' : 'false');
        updateDetailsContent(details, content, false);
      });
    });
  }

  function bindEcomposerAccordion(scope) {
    toArray(scope.querySelectorAll('.ecom-accordion__wrapper, .ecom__accordion')).forEach(function (container) {
      addClass(container, 'ksc-accordion-enhanced');
    });

    toArray(scope.querySelectorAll('.ecom-accordion__item')).forEach(function (item) {
      if (item.dataset.ksEcomAccBound === 'true') return;
      item.dataset.ksEcomAccBound = 'true';

      var title = item.querySelector('.ecom-accordion__title');
      var body = item.querySelector('.ecom-accordion__body');
      if (!title || !body) return;

      addClass(body, 'ksc-accordion-content');
      if (!title.querySelector('.ksc-accordion-icon')) {
        var icon = document.createElement('span');
        icon.className = 'ksc-accordion-icon';
        icon.setAttribute('aria-hidden', 'true');
        title.appendChild(icon);
      }

      title.setAttribute('role', 'button');
      title.setAttribute('aria-expanded', item.classList.contains('ecom-item-active') ? 'true' : 'false');

      var sync = function () {
        title.setAttribute('aria-expanded', item.classList.contains('ecom-item-active') ? 'true' : 'false');
      };

      var observer = new MutationObserver(sync);
      observer.observe(item, { attributes: true, attributeFilter: ['class'] });
    });
  }

  function enhanceLogoMarquee(scope) {
    toArray(scope.querySelectorAll('.ksc-brand-marquee')).forEach(function (section) {
      addClass(section, 'ksc-logo-marquee');
    });
  }

  function updateHeaderState() {
    var scrolled = window.scrollY > 8;
    toArray(document.querySelectorAll('.ksc-header-transition')).forEach(function (header) {
      header.classList.toggle('ksc-header-scrolled', scrolled);
    });
  }

  function init(scope) {
    var rootScope = scope || document;
    root.classList.add('ksc-motion-ready');

    enhanceButtons(rootScope);
    enhanceCardsAndImages(rootScope);
    enhanceHeaderAndNav(rootScope);
    prepareReveal(rootScope);
    setupRevealObserver(rootScope);
    bindDetailsAccordion(rootScope);
    bindEcomposerAccordion(rootScope);
    enhanceLogoMarquee(rootScope);
    updateHeaderState();
  }

  document.addEventListener('DOMContentLoaded', function () {
    init(document);
  });

  window.addEventListener('scroll', updateHeaderState, { passive: true });
  window.addEventListener('resize', updateHeaderState, { passive: true });

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target || document);
    });

    document.addEventListener('shopify:section:reorder', function () {
      init(document);
    });

    document.addEventListener('shopify:block:select', function (event) {
      init((event.target && event.target.closest('.shopify-section')) || document);
    });
  }
})();
